import { cn } from '../../lib/design-tokens';

interface SkeletonProps {
  /** Width class (e.g., 'w-32', 'w-full') */
  width?: string;
  /** Height class (e.g., 'h-4', 'h-8') */
  height?: string;
  /** Whether to render as a circle */
  circle?: boolean;
  /** Number of lines for text skeleton */
  lines?: number;
  /** Additional class names */
  className?: string;
}

/**
 * Skeleton loading component with premium shimmer gradient sweep.
 * Uses a smooth gradient animation instead of simple pulse.
 */
export function SkeletonLoader({
  width = 'w-full',
  height = 'h-4',
  circle = false,
  lines,
  className = '',
}: SkeletonProps): JSX.Element {
  const shimmerClass = 'shimmer animate-shimmer';

  if (circle) {
    return (
      <div
        data-testid="skeleton"
        className={cn(shimmerClass, width, height, 'rounded-full', className)}
      />
    );
  }

  if (lines && lines > 1) {
    return (
      <div data-testid="skeleton" className={cn('space-y-2.5', className)}>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={cn(
              shimmerClass,
              height,
              i === lines - 1 ? 'w-3/4' : width,
              'rounded-lg',
            )}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      data-testid="skeleton"
      className={cn(shimmerClass, width, height, 'rounded-lg', className)}
    />
  );
}

/**
 * Card skeleton for list items.
 */
export function CardSkeleton(): JSX.Element {
  return (
    <div className="card animate-fade-in p-4">
      <div className="flex items-start gap-3">
        <SkeletonLoader circle width="w-9" height="h-9" />
        <div className="flex-1 space-y-2.5">
          <SkeletonLoader width="w-32" height="h-4" />
          <SkeletonLoader width="w-48" height="h-3" />
        </div>
        <SkeletonLoader width="w-16" height="h-5" className="rounded-full" />
      </div>
    </div>
  );
}

/**
 * Panel skeleton for content panels.
 */
export function PanelSkeleton(): JSX.Element {
  return (
    <div className="card animate-fade-in p-5">
      <SkeletonLoader width="w-24" height="h-5" className="mb-4" />
      <SkeletonLoader lines={4} height="h-3" />
    </div>
  );
}
